/* eslint-disable jsx-a11y/no-static-element-interactions */
"use client"

import { useQuery } from "@tanstack/react-query"
import { useAtom } from "jotai"
import { XIcon } from "lucide-react"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { useImagePreloader } from "@/hooks/use-image-preloader"
import { getCurrentAreaInClient } from "@/lib/utils.client"
import { useORPC } from "@/providers/orpc-provider"
import { useTracking } from "@/track/tracking-hooks"
import { CMSImage, CMSLink } from "../components/cms-items"
import { getImageUrlByDevice } from "../utils"
import { useUserState } from "./advertisement-wrapper"
import { advertisementRecordsAtom } from "./store/advertisement-atoms"
import {
	type AdvertisementConfig,
	generateAdvertisementContentHash,
	shouldShowAdvertisementComplete,
} from "./utils/advertisement-helpers"

export function AdvertisementActivity() {
	const [isVisible, setIsVisible] = useState(true)
	const area = getCurrentAreaInClient()
	const pathname = usePathname()
	const orpc = useORPC()
	const { track, TrackingEventType } = useTracking()
	const { preload, statuses, loadedUrls } = useImagePreloader()
	const [advertisementRecords, setAdvertisementRecords] = useAtom(
		advertisementRecordsAtom,
	)

	// 使用统一的用户状态
	const { hasSession, isNewUser } = useUserState()

	const { data: advertisementDataResponse, isLoading } = useQuery({
		...orpc.cms.content.public_findLiveWithPagination.queryOptions({
			input: {
				page: 1,
				pageSize: 10,
				area: area?.toUpperCase(),
				parentAlias: "advertisement-config",
				platform: "web",
			},
		}),
		staleTime: 300000,
	})

	const advertisement = advertisementDataResponse?.items?.find(
		(item) => item.type === "activity-widget",
	) as AdvertisementConfig | undefined

	const image = advertisement?.data?.image
	const link = advertisement?.data?.link

	// 检查是否应该显示广告
	const shouldShow = shouldShowAdvertisementComplete(
		advertisement,
		"web",
		area,
		pathname,
		advertisement?.data?.userType,
		hasSession,
		isNewUser,
		advertisementRecords,
	)

	// 预加载图片
	useEffect(() => {
		if (!image) return

		const imageUrl = getImageUrlByDevice(image)
		if (imageUrl) {
			preload("activity-widget", [imageUrl])
		}
	}, [image, preload])

	// 广告显示时的埋点 + 记录打开时间
	useEffect(() => {
		if (isVisible && advertisement && shouldShow) {
			// 构建跳转URL字符串
			let jumpUrl = ""
			if (link?.type === "external" && link.externalUrl) {
				jumpUrl = link.externalUrl
			} else if (link?.type === "internal" && link.internalUrl) {
				jumpUrl = link.internalUrl
			} else if (link?.type === "collection" && link.collectionId) {
				jumpUrl = `/collections/${link.collectionId}`
			}

			track(
				"PopMartGlobalWebExpandBannerPopupShow",
				{
					jump_url: jumpUrl,
				},
				TrackingEventType.VIEW,
			)

			// 打开时记录时间戳
			const cacheKey = `${advertisement.id}_${area}`
			const contentHash = generateAdvertisementContentHash(advertisement)
			setAdvertisementRecords((prev) => ({
				...prev,
				[cacheKey]: {
					id: advertisement.id,
					area: area,
					lastShown: Date.now(),
					contentHash: contentHash,
				},
			}))
		}
	}, [isVisible, advertisement, shouldShow, track, TrackingEventType, link, area, setAdvertisementRecords])

	const handleClose = (e?: React.MouseEvent) => {
		e?.stopPropagation()
		setIsVisible(false)
		if (advertisement) {
			const cacheKey = `${advertisement.id}_${area}`
			const contentHash = generateAdvertisementContentHash(advertisement)
			setAdvertisementRecords((prev) => ({
				...prev,
				[cacheKey]: {
					id: advertisement.id,
					area: area,
					lastShown: Date.now(),
					contentHash: contentHash, // 广告内容哈希
				},
			}))
		}
	}

	if (!isVisible || isLoading || !advertisement || !shouldShow) {
		return null
	}

	// If there's no image, don't render anything for the activity widget
	if (!image) {
		return null
	}

	// 检查图片是否已加载
	const isImageReady =
		statuses["activity-widget"] === "loaded" && !!loadedUrls["activity-widget"]

	const handleClick = () => {
		// 构建跳转URL字符串
		let jumpUrl = ""
		if (link?.type === "external" && link.externalUrl) {
			jumpUrl = link.externalUrl
		} else if (link?.type === "internal" && link.internalUrl) {
			jumpUrl = link.internalUrl
		} else if (link?.type === "collection" && link.collectionId) {
			jumpUrl = `/collections/${link.collectionId}`
		}

		track(
			"PopMartGlobalWebExpandBannerPopupClick",
			{
				jump_url: jumpUrl,
			},
			TrackingEventType.CLICK,
		)

		// 点击时也记录时间戳（与关闭保持一致）
		if (advertisement) {
			const cacheKey = `${advertisement.id}_${area}`
			const contentHash = generateAdvertisementContentHash(advertisement)
			setAdvertisementRecords((prev) => ({
				...prev,
				[cacheKey]: {
					id: advertisement.id,
					area: area,
					lastShown: Date.now(),
					contentHash: contentHash,
				},
			}))
		}

		// 关闭悬浮窗，让跳转发生
		setIsVisible(false)
	}

	if (!isImageReady) {
		return null
	}

	return (
		<div
			className="fixed right-4 bottom-24 z-30 flex flex-col items-end md:right-8 md:bottom-10"
			onClick={handleClick}
		>
			{/* Close button positioned 8px above the image (via margin) */}
			<button
				type="button"
				onClick={handleClose}
				className="mb-2 rounded-full bg-gray-300 p-0.5 text-white md:p-1"
				aria-label="Close activity widget" // Accessibility label
			>
				<XIcon className="size-3" />
			</button>

			{/* Image container with link */}
			<CMSLink link={link}>
				<CMSImage
					image={image}
					width={120} // Small image, e.g., 96x96px
					height={120}
					className="aspect-square size-24 object-cover md:size-30" // Tailwind for 1:1 aspect ratio and styling
				/>
			</CMSLink>
		</div>
	)
}
